/**
 * Created by neo on 18.01.17.
 */
import { makeAutoObservable } from 'mobx';
import { CoachWorkout, createCoachWorkout } from '../Coach/Program/Workout/CoachWorkout';
import { ExerciseVariation } from '../ProgramPortfolio/ExerciseVariation';
import { ExerciseBlockLog } from './ExerciseBlockLog';
import { ViewPhase, createViewPhase } from './ViewPhase';
import { ViewSet } from './ViewSet';
import { WorkoutLog, createWorkoutLog } from './WorkoutLog';
import { ViewBlock } from './ViewBlock';
import { Athlete } from '../Athlete/Athlete';
import { MicroCycleDay } from '../Coach/Program/MicroCycleDay';
import { EventEntry } from '../Coach/Schedule/EventEntry';

export type ViewWorkout = {
  workoutLog: WorkoutLog;
  workout: CoachWorkout;
  viewPhases: ViewPhase[];
  selectedViewSet?: ViewSet;
  autoPlayEnabled: boolean;
  autoPauseEnabled: boolean;
  inForeground: boolean;
  pauseMode: boolean;
  readonly isInBreak: boolean;
  readonly currentPhase: 'countdown' | 'active' | 'break' | 'finished';
  readonly estimatedTotalExerciseTime: number;
  readonly lastViewPhase: ViewPhase | undefined;
  readonly firstViewBlock: ViewBlock | undefined;
  readonly elapsedTime: number;
  readonly isPaused: boolean;
  readonly totalExercises: number;
  readonly isCompleted: boolean;
  readonly equipmentTypes: string[];
  readonly nonFreeEquipmentTypes: string[];
  readonly exerciseBlocks: ViewBlock[];
  readonly sets: ViewSet[];
  readonly lastSet: ViewSet | undefined;
  readonly active: boolean;
  readonly activeViewSet: ViewSet | undefined;
  readonly currentBlock: ExerciseBlockLog | undefined;
  readonly name: string;
  readonly description: string;
  readonly phaseCount: number;
  readonly exercises: ExerciseVariation[];
  readonly completedExercises: number;
  readonly completedOrStartedExercises: number;
  readonly activeViewSetIndex: number;
  readonly isBreakTime: boolean;
  readonly isPlannedBreakFinished: boolean;
  readonly isPlannedDurationFinishing: boolean;
  readonly isBodyWeight: boolean;
  readonly nextViewSetFromEnd: ViewSet | undefined;
  readonly currentViewSetFromEnd: ViewSet | undefined;
  readonly lastViewSetFromEnd: ViewSet | undefined;
  selectViewSet(viewSet: ViewSet): void;
  replay(): void;
  deleteSet(viewSet: ViewSet): void;
  enableAutoPlay(): void;
  disableAutoPlay(): void;
  enableAutoPause(): void;
  disableAutoPause(): void;
  end(): WorkoutLog;
  terminateAndSave(): Promise<WorkoutLog>;
  startFirstExercise(): ViewSet | undefined;
  startExercise(viewBlock: ViewBlock, startDateTime?: Date, save?: boolean): ViewSet | undefined;
  startSet(viewSet: ViewSet, startDateTime?: Date, save?: boolean): ViewSet | undefined;
  stopWorkout(): WorkoutLog;
  pause(): void;
  resume(): void;
};

export function createViewWorkout(workoutLog: WorkoutLog, workout: CoachWorkout): ViewWorkout {
  const result: ViewWorkout = makeAutoObservable({
    workoutLog,
    workout,
    viewPhases: [] as ViewPhase[],
    selectedViewSet: undefined as ViewSet | undefined,
    autoPlayEnabled: false,
    autoPauseEnabled: false,
    inForeground: true,
    pauseMode: false,

    selectViewSet(viewSet: ViewSet) {
      this.selectedViewSet = viewSet;
    },

    replay() {
      const blocks = this.workoutLog.exerciseBlocks;
      const ignoreIndex = new Array(blocks.length);
      this.viewPhases = this.workout.phases.map((phase) => {
        const viewPhase = createViewPhase(this, phase);
        blocks.forEach((block, index) => {
          if (!ignoreIndex[index] && viewPhase.accepts(block)) {
            ignoreIndex[index] = true;
          }
        });
        viewPhase.replay();
        return viewPhase;
      });
    },

    deleteSet(viewSet: ViewSet) {
      if (viewSet.viewBlock) {
        const index = viewSet.viewBlock.sets.findIndex((s) => s.id === viewSet.id);
        if (index !== -1) viewSet.viewBlock.sets.splice(index, 1);
      }
    },

    enableAutoPlay() {
      this.autoPlayEnabled = true;
    },

    disableAutoPlay() {
      this.autoPlayEnabled = false;
    },

    enableAutoPause() {
      this.autoPauseEnabled = true;
    },

    disableAutoPause() {
      this.autoPauseEnabled = false;
    },

    end(): WorkoutLog {
      this.disableAutoPlay();
      return this.workoutLog.end();
    },

    terminateAndSave(): Promise<WorkoutLog> {
      this.end();
      return this.workoutLog.terminateAndSave();
    },

    startFirstExercise(): ViewSet | undefined {
      const firstSet = this.firstViewBlock?.firstSet;
      if (firstSet) {
        this.selectViewSet(firstSet);
        return this.startSet(firstSet);
      }
      return undefined;
    },

    startExercise(viewBlock: ViewBlock, startDateTime: Date = new Date(), save: boolean = true): ViewSet | undefined {
      if (!this.workoutLog.ended) {
        this.workoutLog.start();
        return viewBlock.start(startDateTime, save);
      }
      return undefined;
    },

    startSet(viewSet: ViewSet, startDateTime: Date = new Date(), save: boolean = true): ViewSet | undefined {
      if (!this.workoutLog.ended) {
        this.workoutLog.start();
        return viewSet.viewBlock.startSet(viewSet, startDateTime, save);
      }
      return undefined;
    },

    stopWorkout(): WorkoutLog {
      this.workoutLog.stopExercise(false);
      return this.end();
    },

    pause() {
      this.activeViewSet?.logSet?.pause();
    },

    resume() {
      this.activeViewSet?.logSet?.resume();
    },

    get isInBreak(): boolean {
      if (!this.active || this.workoutLog.ended) return false;
      const active = this.activeViewSet;
      if (!active?.isFinished) return false;
      return !!active.nextSet;
    },

    get currentPhase(): 'countdown' | 'active' | 'break' | 'finished' {
      if (this.workoutLog.ended) return 'finished';
      if (!this.workoutLog.started) return 'countdown';
      if (this.isInBreak) return 'break';
      return 'active';
    },

    get estimatedTotalExerciseTime(): number {
      return this.workout.estimatedTotalExerciseTime;
    },

    get lastViewPhase(): ViewPhase | undefined {
      return this.viewPhases[this.viewPhases.length - 1];
    },

    get firstViewBlock(): ViewBlock | undefined {
      return this.exerciseBlocks.length > 0 ? this.exerciseBlocks[0] : undefined;
    },

    get elapsedTime(): number {
      return this.workoutLog.totalDuration;
    },

    get isPaused(): boolean {
      return this.activeViewSet?.isPaused || false;
    },

    get totalExercises(): number {
      return this.viewPhases.reduce((total, phase) => total + phase.blockCount, 0);
    },

    get isCompleted(): boolean {
      return this.viewPhases.findIndex((p) => !p.isCompleted) === -1;
    },

    get equipmentTypes(): string[] {
      return this.workout.equipmentTypes;
    },

    get nonFreeEquipmentTypes(): string[] {
      return this.equipmentTypes.filter((e) => e !== 'FREE' && e !== 'FREE_OUTDOOR');
    },

    get exerciseBlocks(): ViewBlock[] {
      return this.viewPhases.reduce((arr, p) => arr.concat(p.exerciseBlocks.slice()), [] as ViewBlock[]);
    },

    get sets(): ViewSet[] {
      return this.exerciseBlocks.reduce((a, b) => a.concat(b.sets.slice()), [] as ViewSet[]);
    },

    get lastSet(): ViewSet | undefined {
      return this.sets.length > 0 ? this.sets[this.sets.length - 1] : undefined;
    },

    get active(): boolean {
      return this.workoutLog.active;
    },

    get activeViewSet(): ViewSet | undefined {
      const currentSet = this.workoutLog.currentSet;
      if (currentSet) return this.sets.find((s) => s.logSet?.setId === currentSet.setId);
      return undefined;
    },

    get currentBlock(): ExerciseBlockLog | undefined {
      return this.workoutLog.lastBlock;
    },

    get name(): string {
      return this.workout.defaultName;
    },

    get description(): string {
      return this.workout.translatedDescription;
    },

    get phaseCount(): number {
      return this.viewPhases.length;
    },

    get exercises(): ExerciseVariation[] {
      return this.viewPhases.reduce((ex, phase) => ex.concat(phase.exercises), [] as ExerciseVariation[]);
    },

    get completedExercises(): number {
      return this.viewPhases.reduce((total, phase) => total + phase.completedExercises, 0);
    },

    get completedOrStartedExercises(): number {
      return this.viewPhases.reduce((total, phase) => total + phase.completedOrStartedExercises, 0);
    },

    get activeViewSetIndex(): number {
      if (this.activeViewSet) return this.sets.findIndex((vs) => vs.id === this.activeViewSet?.id);
      return 0;
    },

    get isBreakTime(): boolean {
      if (this.activeViewSet?.logSet && this.activeViewSet?.isRunning && (this.activeViewSet?.plannedDuration ?? 0) > 0) {
        return Math.floor((this.activeViewSet?.duration ?? 0) / 1000) === Math.floor(this.activeViewSet?.plannedDuration ?? 0);
      }
      return false;
    },

    get isPlannedBreakFinished(): boolean {
      if ((this.activeViewSet?.plannedBreakTime ?? 0) > 0) {
        return Math.floor((this.activeViewSet?.breakTime ?? 0) / 1000) === Math.floor(this.activeViewSet?.plannedBreakTime ?? 0);
      }
      return false;
    },

    get isPlannedDurationFinishing(): boolean {
      if (this.activeViewSet?.logSet && this.activeViewSet?.isRunning && (this.activeViewSet?.plannedDuration ?? 0) > 0) {
        return Math.floor((this.activeViewSet?.duration ?? 0) / 1000) === Math.floor(this.activeViewSet?.plannedDuration ?? 0) - 5;
      }
      return false;
    },

    get isBodyWeight(): boolean {
      return this.workout.isBodyWeight;
    },

    get nextViewSetFromEnd(): ViewSet | undefined {
      if (this.sets[this.sets.length - 1]?.isFinished) return undefined;
      return this.sets.slice().reverse().find((s, index, arr) => !s.isStarted && (!arr[index + 1] || arr[index + 1].isFinished));
    },

    get currentViewSetFromEnd(): ViewSet | undefined {
      if (this.sets[this.sets.length - 1]?.isFinished) return undefined;
      return this.sets.slice().reverse().find((s, index, arr) => !s.isFinished && (!arr[index + 1] || arr[index + 1].isFinished));
    },

    get lastViewSetFromEnd(): ViewSet | undefined {
      if (this.sets[this.sets.length - 1]?.isFinished) return undefined;
      return this.sets.slice().reverse().find((s, index, arr) => s.isStarted && (!arr[index + 1] || arr[index + 1].isFinished)) ?? this.nextViewSetFromEnd;
    },
  } as ViewWorkout);

  // Initialize viewPhases
  result.replay();

  return result;
}

export const ViewWorkoutApi = {
  async create(
    athlete: Athlete,
    workout: CoachWorkout,
    microCycleDay?: MicroCycleDay,
    eventEntry?: EventEntry,
  ): Promise<ViewWorkout> {
    const workoutLog = createWorkoutLog({
      athlete,
      coachProgramId: microCycleDay?.athleteSuperMacroId,
      microCycleDay,
      workout,
      mesoCycleId: microCycleDay?.mesoCycleId,
      microCycleId: microCycleDay?.microCycleId,
      microCycleDayId: microCycleDay?.id,
      workoutId: workout.id,
      eventEntry,
    } as any);
    const resolvedWorkout = microCycleDay?.workout ?? workout;
    return createViewWorkout(workoutLog, resolvedWorkout ?? createCoachWorkout());
  },

  async fromMicroCycleDay(athlete: Athlete, microCycleDay: MicroCycleDay): Promise<ViewWorkout> {
    const workout = microCycleDay.workout ?? createCoachWorkout();
    return ViewWorkoutApi.create(athlete, workout, microCycleDay);
  },
};
